import React, { useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { FaEnvelope, FaArrowLeft, FaPaperPlane } from "react-icons/fa";
import { Link } from "react-router-dom";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors({ ...errors, email: "" });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);

      // Simulate API call
      setTimeout(() => {
        console.log("Password reset requested for:", email);
        setIsSubmitted(true);
        setIsLoading(false);
      }, 1500);
    } else {
      setErrors(newErrors);
    }
  };

  const handleResetAnother = () => {
    setIsSubmitted(false);
    setEmail("");
    setErrors({});
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password | Trans World Shipping</title>
        <meta
          name="description"
          content="Reset your password for Trans World Shipping account. Enter your email address to receive password reset instructions."
        />
        <link
          rel="canonical"
          href="https://transworldshippingcompany.com/forgot-password"
        />
      </Helmet>

      <TheForgotPassword>
        <div className="forgot-container">
          <div className="forgot-card">
            <Link to="/login" className="back-link">
              <FaArrowLeft /> Back to Login
            </Link>

            {!isSubmitted ? (
              <>
                <div className="forgot-header">
                  <h1>Forgot Password?</h1>
                  <p>
                    No worries! Enter your email address and we'll send you
                    instructions to reset your password.
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="email">
                      Email Address <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaEnvelope className="input-icon" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className={errors.email ? "error" : ""}
                        disabled={isLoading}
                      />
                    </div>
                    {errors.email && (
                      <span className="error-message">{errors.email}</span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="reset-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane /> Send Reset Instructions
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="success-section">
                <div className="success-icon">✓</div>
                <h2>Check Your Email</h2>
                <p>
                  We've sent password reset instructions to{" "}
                  <strong>{email}</strong>
                </p>
                <p className="success-message">
                  Please check your inbox and follow the link to reset your
                  password. The link will expire in 1 hour for security reasons.
                </p>
                <div className="success-actions">
                  <button
                    onClick={handleResetAnother}
                    className="reset-another-btn"
                  >
                    Send to Another Email
                  </button>
                  <Link to="/login" className="login-redirect">
                    Return to Login
                  </Link>
                </div>
              </div>
            )}

            <div className="help-section">
              <p>
                Need help? <Link to="/contact">Contact Support</Link>
              </p>
            </div>
          </div>
        </div>
      </TheForgotPassword>
    </>
  );
};

const TheForgotPassword = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  .forgot-container {
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
  }

  .forgot-card {
    background: white;
    border-radius: 20px;
    padding: 2.5rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    transition: transform 0.3s ease;
    position: relative;

    &:hover {
      transform: translateY(-5px);
    }
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #718096;
    text-decoration: none;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
    transition: color 0.3s ease;

    &:hover {
      color: #d4af37;
    }
  }

  .forgot-header {
    text-align: center;
    margin-bottom: 2rem;

    h1 {
      font-size: 1.8rem;
      color: #0b1e33;
      font-weight: 700;
      margin-bottom: 0.75rem;
    }

    p {
      font-size: 0.95rem;
      color: #718096;
      line-height: 1.5;
    }
  }

  .form-group {
    margin-bottom: 1.5rem;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
      color: #0b1e33;
      font-size: 0.9rem;

      .required {
        color: #e53e3e;
        margin-left: 2px;
      }
    }

    .input-wrapper {
      position: relative;

      .input-icon {
        position: absolute;
        left: 1rem;
        top: 50%;
        transform: translateY(-50%);
        color: #a0aec0;
        font-size: 1rem;
        pointer-events: none;
        z-index: 1;
      }

      input {
        width: 100%;
        padding: 0.85rem 1rem 0.85rem 2.75rem;
        border: 1.5px solid #e2e8f0;
        border-radius: 10px;
        font-size: 1rem;
        transition: all 0.3s ease;
        font-family: inherit;

        &:focus {
          outline: none;
          border-color: #d4af37;
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
        }

        &.error {
          border-color: #e53e3e;

          &:focus {
            box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
          }
        }

        &:disabled {
          background: #f7fafc;
          cursor: not-allowed;
        }
      }
    }

    .error-message {
      display: block;
      margin-top: 0.5rem;
      font-size: 0.8rem;
      color: #e53e3e;
    }
  }

  .reset-btn {
    width: 100%;
    background: #d4af37;
    color: #0b1e33;
    border: none;
    padding: 0.85rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    &:hover {
      background: #c4a12e;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(212, 175, 55, 0.3);
    }

    &:active {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }

    .spinner {
      display: inline-block;
      width: 18px;
      height: 18px;
      border: 2px solid #0b1e33;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .success-section {
    text-align: center;

    .success-icon {
      width: 70px;
      height: 70px;
      background: #4caf50;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2.5rem;
      font-weight: bold;
      margin: 0 auto 1.5rem;
      animation: scaleIn 0.3s ease;
    }

    @keyframes scaleIn {
      from {
        transform: scale(0);
      }
      to {
        transform: scale(1);
      }
    }

    h2 {
      font-size: 1.5rem;
      color: #0b1e33;
      margin-bottom: 0.75rem;
      font-weight: 700;
    }

    p {
      font-size: 0.95rem;
      color: #4a5568;
      margin-bottom: 0.5rem;
      line-height: 1.5;

      strong {
        color: #0b1e33;
      }

      &.success-message {
        margin-top: 1rem;
        padding: 1rem;
        background: #f0fff4;
        border-radius: 10px;
        color: #2f855a;
        font-size: 0.9rem;
        border-left: 3px solid #4caf50;
      }
    }

    .success-actions {
      margin-top: 2rem;
      display: flex;
      gap: 1rem;
      justify-content: center;
      flex-wrap: wrap;

      .reset-another-btn {
        background: transparent;
        border: 1.5px solid #d4af37;
        color: #d4af37;
        padding: 0.7rem 1.5rem;
        border-radius: 8px;
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        font-family: inherit;

        &:hover {
          background: #d4af37;
          color: #0b1e33;
        }
      }

      .login-redirect {
        background: #d4af37;
        color: #0b1e33;
        text-decoration: none;
        padding: 0.7rem 1.5rem;
        border-radius: 8px;
        font-size: 0.9rem;
        font-weight: 600;
        transition: all 0.3s ease;

        &:hover {
          background: #c4a12e;
          transform: translateY(-2px);
        }
      }
    }
  }

  .help-section {
    text-align: center;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e2e8f0;

    p {
      font-size: 0.9rem;
      color: #718096;

      a {
        color: #d4af37;
        text-decoration: none;
        font-weight: 600;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    padding: 1rem;

    .forgot-card {
      padding: 1.5rem;
    }

    .forgot-header h1 {
      font-size: 1.5rem;
    }

    .forgot-header p {
      font-size: 0.85rem;
    }

    .success-section h2 {
      font-size: 1.3rem;
    }

    .success-section .success-icon {
      width: 60px;
      height: 60px;
      font-size: 2rem;
    }

    .success-actions {
      flex-direction: column;

      .reset-another-btn,
      .login-redirect {
        width: 100%;
        text-align: center;
      }
    }
  }

  @media screen and (max-width: 480px) {
    .forgot-card {
      padding: 1.25rem;
    }

    .forgot-header h1 {
      font-size: 1.3rem;
    }
  }
`;

export default ForgotPassword;
